/**
 * postListShared.js
 * ─────────────────
 * Logic shared by postListBlock.vue (public) and postListBlockAdmin.vue
 * (editor): request building, item normalisation, layout helpers.
 * No Vue imports.
 */

/** Public posts endpoint (Website SPA) and editor endpoint (Application). */
export var PUBLIC_POSTS_URL = '/ajax/getWebsitePosts.php';
export var ADMIN_POSTS_URL = '/websites/ajax/getWebsitePosts.php';

/**
 * Normalises the `source` config value. Legacy strings and partial objects
 * become { mode, categoryId, authorId, tagId, sinceDays, tag, postIds }.
 *   mode:      latest | context | filter | picked | trending | related  (legacy: category, tag → filter)
 *   filter:    any mix of categoryId, authorId, tagId, sinceDays
 *   picked:    postIds in the order given
 *   trending:  most viewed in trendingDays (1, 7, 30)
 *   related:   same category or a shared tag as the current article
 *   sponsored: include | only | exclude   format: '' | article | video | audio  (apply to every mode)
 */
export function normalizeSource(source) {
	var out = { mode: 'latest', categoryId: null, authorId: null, tagId: null, sinceDays: 0, tag: '', postIds: [], trendingDays: 7, sponsored: 'include', format: '' };
	if (!source) return out;
	if (typeof source === 'string') {
		try { source = JSON.parse(source); } catch (e) { return out; }
	}
	if (typeof source !== 'object') return out;
	var id = function (v) { return v !== undefined && v !== null && v !== '' ? v : null; };
	out.mode = source.mode || 'latest';
	if (out.mode === 'category' || out.mode === 'tag') out.mode = 'filter';
	out.categoryId = id(source.categoryId);
	out.authorId = id(source.authorId);
	out.tagId = id(source.tagId);
	out.sinceDays = parseInt(source.sinceDays, 10) > 0 ? parseInt(source.sinceDays, 10) : 0;
	out.tag = source.tag || '';
	out.postIds = Array.isArray(source.postIds) ? source.postIds.slice() : [];
	out.trendingDays = [1, 7, 30].indexOf(parseInt(source.trendingDays, 10)) !== -1 ? parseInt(source.trendingDays, 10) : 7;
	out.sponsored = ['include', 'only', 'exclude'].indexOf(source.sponsored) !== -1 ? source.sponsored : 'include';
	out.format = ['article', 'video', 'audio'].indexOf(source.format) !== -1 ? source.format : '';
	return out;
}

/** Human summary of a source, for rail cards and the picker. */
export function describeSource(source, lookup) {
	var s = normalizeSource(source);
	lookup = lookup || {};
	var suffix = [];
	if (s.sponsored === 'only') suffix.push('sponsored');
	if (s.sponsored === 'exclude') suffix.push('no sponsored');
	if (s.format) suffix.push(s.format);
	var tail = suffix.length ? ' · ' + suffix.join(' · ') : '';
	if (s.mode === 'context') return 'This page' + tail;
	if (s.mode === 'trending') return 'Trending · ' + (s.trendingDays === 1 ? 'today' : s.trendingDays + ' days') + tail;
	if (s.mode === 'related') return 'Related to this article' + tail;
	if (s.mode === 'picked') return (s.postIds.length ? s.postIds.length + ' picked' : 'Pick posts') + tail;
	if (s.mode === 'filter') {
		var parts = [];
		if (s.categoryId) parts.push(lookup['category:' + s.categoryId] || 'Category');
		if (s.authorId) parts.push(lookup['author:' + s.authorId] || 'Author');
		if (s.tagId) parts.push(lookup['tag:' + s.tagId] || 'Tag');
		if (s.sinceDays) parts.push('last ' + s.sinceDays + ' days');
		return (parts.length ? parts.join(' · ') : 'Filter') + tail;
	}
	return 'Latest' + tail;
}

/**
 * Builds the query string for getWebsitePosts.php.
 * @param {Object} source   normalized source
 * @param {number} limit
 * @param {number} page
 * @param {Object} [extra]  additional params (websiteID, status)
 */
export function buildPostsQuery(source, limit, page, extra) {
	var params = new URLSearchParams();
	params.append('limit', String(limit));
	params.append('page', String(page));
	if (source.mode === 'filter') {
		if (source.categoryId) params.append('categoryID', String(source.categoryId));
		if (source.authorId) params.append('authorID', String(source.authorId));
		if (source.tagId) params.append('tagID', String(source.tagId));
		if (source.sinceDays) params.append('sinceDays', String(source.sinceDays));
		if (!source.tagId && source.tag) params.append('tag', source.tag); // legacy slug
	}
	if (source.mode === 'picked' && source.postIds.length) params.append('postIDs', source.postIds.join(','));
	if (source.mode === 'trending') params.append('trendingDays', String(source.trendingDays || 7));
	if (source.mode === 'related' && extra && extra.relatedToPostID) params.append('relatedToPostID', String(extra.relatedToPostID));
	if (source.sponsored && source.sponsored !== 'include') params.append('sponsored', source.sponsored);
	if (source.format) params.append('format', source.format);
	params.append('sortBy', 'websitePostDate');
	params.append('sortDir', 'DESC');
	if (extra) {
		Object.keys(extra).forEach(function (k) {
			if (k === 'relatedToPostID') return;
			if (extra[k] !== undefined && extra[k] !== null && extra[k] !== '') params.append(k, String(extra[k]));
		});
	}
	return params.toString();
}

/** First author / category label from the API item shape. */
export function firstLabel(list) {
	if (!Array.isArray(list) || !list.length) return '';
	var x = list[0];
	return (x && (x.label || x.taxonomyLabel || x.name)) || '';
}

/** Items the user has not published yet (editor shows them with a badge). */
export function isDraftItem(item) {
	return !!(item && item.websitePostStatus && item.websitePostStatus !== 'publish');
}

/**
 * Splits items for the hero layout: first item large, the rest beside it.
 * @returns {{ lead: Object|null, rest: Array }}
 */
export function splitHero(items) {
	if (!items.length) return { lead: null, rest: [] };
	return { lead: items[0], rest: items.slice(1) };
}
